import React, { useState, useEffect, useCallback } from 'react';
import { productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Search, Filter, GraduationCap, Smartphone, Monitor, Wifi } from 'lucide-react';
import { toast } from 'sonner';

// ── Constants ──────────────────────────────────────────────────────────────

const CATEGORIES = ['Formation', 'Application Mobile'];

const FORMATION_TYPES = ['À distance', 'Présentielle'];

const APP_NAMES = ['Elikalab', 'Warlex', 'Elikalab Studio', 'Warlex Studio', 'TradOps'];

const PLATFORMS = ['iOS', 'Android', 'iOS & Android'];

const EMPTY_FORM = {
  name: '',
  sku: '',
  category: 'Formation',
  brand: '',       // type (formation) or app name (app)
  supplier: '',    // platform (app only)
  price: '',
  description: '',
  status: 'active',
  stock_quantity: '0',
  low_stock_threshold: '0',
};

// ── Sub-components ─────────────────────────────────────────────────────────

function CategoryIcon({ category }) {
  if (category === 'Formation') return <GraduationCap size={16} className="text-violet-500" />;
  return <Smartphone size={16} className="text-blue-500" />;
}

function PlatformBadge({ platform }) {
  if (!platform) return null;
  const styles = {
    'iOS': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    'Android': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    'iOS & Android': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[platform] || styles['iOS']}`}>
      {platform}
    </span>
  );
}

function FormationTypeBadge({ type }) {
  if (!type) return null;
  const styles = {
    'À distance': 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    'Présentielle': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[type] || 'bg-slate-100 text-slate-600'}`}>
      {type}
    </span>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
      status === 'active'
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
    }`}>
      {status === 'active' ? 'Actif' : 'Inactif'}
    </span>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterBrand !== 'all') params.brand = filterBrand;
      const res = await productsAPI.list(params);
      setProducts(res.data);
    } catch {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterBrand]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Derived lists
  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))];

  // Local search
  const displayed = products.filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(search.toLowerCase())
  );

  // KPIs
  const formations = products.filter(p => p.category === 'Formation').length;
  const applications = products.filter(p => p.category === 'Application Mobile').length;
  const iosCount = products.filter(p => p.supplier && p.supplier.includes('iOS')).length;
  const androidCount = products.filter(p => p.supplier && p.supplier.includes('Android')).length;

  const openCreate = () => {
    setEditingProduct(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setForm({
      name: p.name,
      sku: p.sku,
      category: p.category || 'Formation',
      brand: p.brand || '',
      supplier: p.supplier || '',
      price: String(p.price),
      description: p.description || '',
      status: p.status,
      stock_quantity: '0',
      low_stock_threshold: '0',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price) || 0,
        stock_quantity: 0,
        low_stock_threshold: 0,
        category: form.category || null,
        brand: form.brand || null,
        supplier: form.category === 'Application Mobile' ? (form.supplier || null) : null,
        description: form.description || null,
      };
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Produit mis à jour');
      } else {
        await productsAPI.create(data);
        toast.success('Produit créé');
      }
      setDialogOpen(false);
      loadProducts();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Produit supprimé');
      loadProducts();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-52" />
          <Skeleton className="h-10 w-44" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Catalogue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Formations et applications digitales</p>
        </div>
        <Button onClick={openCreate} size="sm" className="gap-2">
          <Plus size={15} />
          Ajouter
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Formations', value: formations, icon: GraduationCap, color: 'text-violet-500' },
          { label: 'Applications', value: applications, icon: Smartphone, color: 'text-blue-500' },
          { label: 'iOS', value: iosCount, icon: Monitor, color: 'text-slate-500' },
          { label: 'Android', value: androidCount, icon: Wifi, color: 'text-emerald-500' },
        ].map(k => (
          <div key={k.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <k.icon size={20} className={k.color} />
            <div>
              <p className="text-xs text-muted-foreground">{k.label}</p>
              <p className="text-xl font-bold text-foreground">{k.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters + content */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar filters — desktop */}
        <aside className="hidden lg:block w-48 flex-shrink-0 space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Catégorie</p>
            <div className="space-y-1">
              {['all', ...CATEGORIES].map(cat => (
                <button
                  key={cat}
                  onClick={() => { setFilterCategory(cat); setFilterBrand('all'); }}
                  className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${
                    filterCategory === cat ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {cat === 'all' ? 'Toutes' : cat}
                </button>
              ))}
            </div>
          </div>

          {(filterCategory === 'all' || filterCategory === 'Application Mobile') && brands.filter(b => APP_NAMES.includes(b)).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Application</p>
              <div className="space-y-1">
                <button
                  onClick={() => setFilterBrand('all')}
                  className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${filterBrand === 'all' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  Toutes
                </button>
                {APP_NAMES.filter(a => brands.includes(a)).map(app => (
                  <button
                    key={app}
                    onClick={() => setFilterBrand(app)}
                    className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${filterBrand === app ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    {app}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(filterCategory === 'all' || filterCategory === 'Formation') && brands.filter(b => FORMATION_TYPES.includes(b)).length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Type</p>
              <div className="space-y-1">
                <button
                  onClick={() => setFilterBrand('all')}
                  className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${filterBrand === 'all' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  Tous
                </button>
                {FORMATION_TYPES.filter(t => brands.includes(t)).map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterBrand(type)}
                    className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${filterBrand === type ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Search + mobile filter toggle */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden gap-1.5"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={14} />
              Filtres
            </Button>
          </div>

          {/* Mobile filters */}
          {showFilters && (
            <div className="lg:hidden flex flex-wrap gap-2">
              <Select value={filterCategory} onValueChange={v => { setFilterCategory(v); setFilterBrand('all'); }}>
                <SelectTrigger className="w-40 h-8 text-xs">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              {brands.length > 0 && (
                <Select value={filterBrand} onValueChange={setFilterBrand}>
                  <SelectTrigger className="w-36 h-8 text-xs">
                    <SelectValue placeholder="Filtrer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* ── Mobile card list ── */}
          <div className="md:hidden space-y-3">
            {displayed.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">Aucun produit trouvé</div>
            ) : (
              displayed.map(p => (
                <div key={p.id} className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CategoryIcon category={p.category} />
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground text-sm leading-tight">{p.name}</p>
                        <p className="text-xs font-mono text-muted-foreground">{p.sku}</p>
                      </div>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.category === 'Application Mobile' ? (
                      <>
                        {p.brand && <span className="text-xs text-muted-foreground">{p.brand}</span>}
                        {p.supplier && <PlatformBadge platform={p.supplier} />}
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-muted-foreground">{p.category}</span>
                        {p.brand && <FormationTypeBadge type={p.brand} />}
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-border">
                    <span className="font-bold text-foreground">
                      {Number(p.price).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(p)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Desktop table ── */}
          <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Produit</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Catégorie</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Type / Plateforme</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Prix</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Statut</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayed.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-muted-foreground">
                        <GraduationCap size={32} className="mx-auto mb-2 opacity-30" />
                        Aucun produit trouvé
                      </td>
                    </tr>
                  ) : (
                    displayed.map(p => (
                      <tr key={p.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <CategoryIcon category={p.category} />
                            <div>
                              <p className="font-semibold text-foreground">{p.name}</p>
                              <p className="text-xs font-mono text-muted-foreground">{p.sku}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-sm">{p.category || '—'}</td>
                        <td className="px-4 py-3">
                          {p.category === 'Application Mobile' ? (
                            <div className="flex items-center gap-2">
                              {p.brand && <span className="text-sm text-muted-foreground">{p.brand}</span>}
                              {p.supplier && <PlatformBadge platform={p.supplier} />}
                            </div>
                          ) : (
                            <FormationTypeBadge type={p.brand} />
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {Number(p.price).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StatusBadge status={p.status} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(p)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* CRUD Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Catégorie */}
            <div>
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={v => setField('category', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Champs conditionnels selon la catégorie */}
            {form.category === 'Formation' && (
              <div>
                <Label>Type de formation *</Label>
                <Select value={form.brand} onValueChange={v => setField('brand', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner le type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.category === 'Application Mobile' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Application *</Label>
                  <Select value={form.brand} onValueChange={v => setField('brand', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir l'app" />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_NAMES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Plateforme *</Label>
                  <Select value={form.supplier} onValueChange={v => setField('supplier', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="iOS / Android" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Nom */}
            <div>
              <Label htmlFor="p-name">Nom du produit *</Label>
              <Input
                id="p-name"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                required
                placeholder={
                  form.category === 'Formation'
                    ? `Formation ${form.brand || '...'}`
                    : form.brand && form.supplier
                    ? `${form.brand} ${form.supplier}`
                    : 'Nom du produit'
                }
              />
            </div>

            {/* SKU + Prix */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="p-sku">SKU</Label>
                <Input id="p-sku" value={form.sku} onChange={e => setField('sku', e.target.value)} placeholder="PRD-00001" />
              </div>
              <div>
                <Label htmlFor="p-price">Prix (€) *</Label>
                <Input id="p-price" type="number" step="0.01" min="0" value={form.price} onChange={e => setField('price', e.target.value)} required />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="p-description">Description</Label>
              <Textarea id="p-description" value={form.description} onChange={e => setField('description', e.target.value)} rows={3} />
            </div>

            {/* Statut */}
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={v => setField('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="inactive">Inactif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Enregistrement...' : (editingProduct ? 'Mettre à jour' : 'Créer')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
